/**
 * TodaySummary コンポーネント
 *
 * 陸送員向けダッシュボードの本日のサマリー表示
 * 案件数、完了数、進捗率を表示
 */

import React, { useMemo } from 'react';
import { Case } from '@/types';

interface TodaySummaryProps {
  /** 案件一覧 */
  cases: Case[];
  /** 追加クラス名 */
  className?: string;
}

/**
 * TodaySummary コンポーネント
 *
 * 本日の配車案件の統計情報を表示：
 * - 本日の案件数
 * - 完了した案件数
 * - 完了率
 */
export const TodaySummary: React.FC<TodaySummaryProps> = ({ cases, className = '' }) => {
  // 統計情報を計算
  const stats = useMemo(() => {
    const totalCases = cases.length;

    // 完了した案件（completed, reported）
    const completedCases = cases.filter(
      c => c.status === 'completed' || c.status === 'reported'
    ).length;

    // 完了率
    const completionRate = totalCases === 0 ? 0 : Math.round((completedCases / totalCases) * 100);

    // 進行中の案件（picked_up, in_transit）
    const inProgressCases = cases.filter(
      c => c.status === 'picked_up' || c.status === 'in_transit'
    ).length;

    // 未開始の案件（provisional, confirmed, assigned）
    const pendingCases = cases.filter(
      c => c.status === 'provisional' || c.status === 'confirmed' || c.status === 'assigned'
    ).length;

    return {
      totalCases,
      completedCases,
      completionRate,
      inProgressCases,
      pendingCases,
    };
  }, [cases]);

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}
    >
      <h2 className='text-lg font-bold text-gray-900 dark:text-white mb-4'>📊 本日のサマリー</h2>

      {/* 統計グリッド */}
      <div className='grid grid-cols-2 gap-4 mb-4'>
        {/* 案件総数 */}
        <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700'>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>案件数</p>
          <p className='text-3xl font-bold text-blue-600 dark:text-blue-400'>{stats.totalCases}</p>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>件</p>
        </div>

        {/* 完了数 */}
        <div className='bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700'>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>完了</p>
          <p className='text-3xl font-bold text-green-600 dark:text-green-400'>
            {stats.completedCases}
          </p>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>件</p>
        </div>

        {/* 進行中 */}
        <div className='bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700'>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>進行中</p>
          <p className='text-3xl font-bold text-amber-600 dark:text-amber-400'>
            {stats.inProgressCases}
          </p>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>件</p>
        </div>

        {/* 待機中 */}
        <div className='bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700'>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>待機中</p>
          <p className='text-3xl font-bold text-purple-600 dark:text-purple-400'>
            {stats.pendingCases}
          </p>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>件</p>
        </div>
      </div>

      {/* 完了率プログレスバー */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600'>
        <div className='flex justify-between items-center mb-2'>
          <p className='text-sm font-medium text-gray-700 dark:text-gray-300'>本日の完了率</p>
          <p className='text-sm font-bold text-gray-900 dark:text-white'>{stats.completionRate}%</p>
        </div>

        {/* プログレスバー */}
        <div className='w-full bg-gray-300 dark:bg-gray-600 rounded-full h-3 overflow-hidden'>
          <div
            className='bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 h-full rounded-full transition-all duration-300'
            style={{ width: `${stats.completionRate}%` }}
          />
        </div>

        {/* 詳細 */}
        <p className='text-xs text-gray-600 dark:text-gray-400 mt-2'>
          {stats.completedCases}/{stats.totalCases}件完了
        </p>
      </div>
    </div>
  );
};

export default TodaySummary;
